import { StatelessComponent } from 'react'
import * as capitalize from 'lodash/capitalize'
import * as identity from 'lodash/identity'
import * as moment from 'moment'
import { DATE_FORMAT_US } from 'constants/datetime'
import { CollectionCategory } from 'interfaces/meta'
import { SortingProp } from 'interfaces/table'
import { formatUSDate } from 'helpers/dateTimeHelpers'
import { BodyText } from 'components/atoms/Text'
import Icon from 'components/atoms/Icon'
import AbsentValue from 'components/atoms/AbsentValue'
import TextButton from 'components/atoms/buttons/TextButton'
import Table, { SAME_SORTING_SELECTOR, TableColumnDefinition, TableComponentType } from 'components/molecules/tables/Table'
import * as styles from 'components/features/pfm/BalanceAccountSettingsModule/BalanceAccountSettingsModule.css'
import { LinkedMixedOperation, MixedBalanceAccount, ProviderCollection } from 'interfaces/linkage'
import AccountBadge, { AccountBadgeProps } from 'components/features/pfm/AccountBadge/AccountBadge'
import TooltipIcon from 'components/features/pfm/Tooltip/TooltipIcon'
import { INACTIVE_INSTITUTION_HINT } from 'constants/hints'
import { renderSnippet } from 'tangle-shared'
import { INACTIVE_LINK_STATUS } from 'constants/accounts'

export type BalanceAccountSettingsStateProps = {
  isMobile: boolean
  isLiveUpdateStarted: boolean
  balanceAccounts: MixedBalanceAccount[]
  providers: ProviderCollection
  categories: CollectionCategory
}

export type BalanceAccountSettingsDispatchProps = {
  onPerformOperation: (account: MixedBalanceAccount, operation: LinkedMixedOperation) => void
}

type BalanceAccountSettingsProps = BalanceAccountSettingsStateProps & BalanceAccountSettingsDispatchProps

const PRELOADER_WIDTH = '21%'

const ACCOUNT_NAME_COLUMN: TableColumnDefinition<MixedBalanceAccount, string> = {
  name: 'name',
  title: (
    <span>
      <span className={ styles.visibleTwoColumnsUp }>Account</span> Name
    </span>
  ),
  propsSelector: ({ name, ...account }) => (
    `${ name }${ !account.is_manual && account.number ? '-' + account.number : '' }`
  ),
  sortingSelector: SAME_SORTING_SELECTOR,
}

const statusComponent = ({ link_status, last_updated }: any) => {
  const date = last_updated !== null ? formatUSDate(last_updated) : <AbsentValue children='Not updated yet' />
  return (
    <div>
      <div>{ capitalize(link_status) }</div>
      <div className={ styles.lastUpdated }>as of { date }</div>
    </div>
  )}

const STATUS_COLUMN: TableColumnDefinition<MixedBalanceAccount, string> = {
  name: 'status',
  title: 'Status',
  component: statusComponent,
  sortingSelector: ({ link_status }) => link_status,
}

const TypedTable = Table as TableComponentType<MixedBalanceAccount>

// TODO: rewrite to `Table2`
const BalanceAccountSettingsModule: StatelessComponent<BalanceAccountSettingsProps> = (props) => {
  const {
    isMobile,
    isLiveUpdateStarted,
    providers,
    balanceAccounts,
    categories,

    onPerformOperation,
  } = props

  if (balanceAccounts.length === 0) {
    return (
      <BodyText className={ styles.emptyState }>
        You have no linked accounts.
      </BodyText>
    )
  }

  const getProviderName = (account: MixedBalanceAccount): string =>
    account.is_manual ? 'Manual Entry' : providers.byKey[account.providerCid].name

  const PROVIDER_NAME_COLUMN: TableColumnDefinition<MixedBalanceAccount, AccountBadgeProps> = {
    name: 'provider-name',
    title: 'Institution',
    component: ({ name, tooltipChild }) => (
      <div className={ styles.accountBadgeCell }>
        <AccountBadge name={ name } tooltipChild={ tooltipChild } />
      </div>
    ),
    propsSelector: (account) => {
      const { is_manual, link_status } = account
      return {
        name: is_manual ? 'Manual Entry' : getProviderName(account),
        tooltipChild: renderSnippet(
          () => (
            <TooltipIcon theme='danger'>
              { INACTIVE_INSTITUTION_HINT }
            </TooltipIcon>
          ),
          link_status === 'inactive',
        ),
      }
    },
    sortingSelector: getProviderName,
    collapseSame: ({ link_status, ...account }) => `${ !account.is_manual && `${ account.providerCid } ` }${ link_status === INACTIVE_LINK_STATUS }`,
  }

  const ACCOUNT_CATEGORY_COLUMN: TableColumnDefinition<MixedBalanceAccount, string> = {
    name: 'account-category',
    title: (
      <span>
        <span className={ styles.visibleTwoColumnsUp }>Account</span> Category
      </span>
    ),
    propsSelector: ({ category }) => categories.byKey[category]?.value,
    sortingSelector: SAME_SORTING_SELECTOR,
  }

  const SPACER_COLUMN: TableColumnDefinition<MixedBalanceAccount, string> = {
    name: 'spacer',
    title: '',
    component: () => null,
  }

  const LIVE_UPDATE_PRELOADER_COLUMN: TableColumnDefinition<MixedBalanceAccount, {}> = {
    name: 'live-update-preloader',
    title: '',
    component: () => (
      <div className={ styles.liveUpdatePreloader }>
        <span className={ styles.liveUpdatePreloaderText }>Updating...</span>
        <div className={ styles.liveUpdatePreloaderIcon }><Icon kind='spinner-light' /></div>
      </div>
    ),
    width: PRELOADER_WIDTH,
  }

  const UPDATE_CREDENTIALS_BUTTON: TableColumnDefinition<MixedBalanceAccount, MixedBalanceAccount> = {
    name: 'update-credentials',
    title: '',
    component: (account) => {
      if (account.is_manual && account.unlinked_at) {
        return (
          <AbsentValue children={ `Unlinked on ${ moment(account.unlinked_at).format(DATE_FORMAT_US) }` } />
        )
      }

      const handleClick = () => onPerformOperation(account, 'update')

      return (
        <TextButton onClick={ handleClick } qaMarker='update-credentials-button'>
          Update
        </TextButton>
      )},
    propsSelector: identity,
    getColSpan: (account) => account.is_manual && account.unlinked_at ? 2 : 1,
  }

  const LINK_BUTTON: TableColumnDefinition<MixedBalanceAccount, MixedBalanceAccount> = {
    name: 'link',
    title: '',
    component: (account) => {
      const { is_manual, link_status } = account
      const isUnlinked = link_status === 'unlinked'

      if (is_manual && isUnlinked) {
        // Something needs to be passed for a cell to be rendered which is necessary for the nth-child rules to behave correctly
        return isMobile ? <div /> : null
      }

      const operation: LinkedMixedOperation = isUnlinked ? 'link' : 'unlink'

      const actionCreator = () => onPerformOperation(account, operation)

      return (
        <TextButton onClick={ actionCreator } qaMarker={ `${ isUnlinked ? 'link' : 'unlink' }-button` }>
          { isUnlinked ? 'Link' : 'Unlink' }
        </TextButton>
      )
    },
    propsSelector: identity,
    getColSpan: (account) => account.is_manual && account.unlinked_at ? 0 : 1,
  }

  const DELETE_BUTTON: TableColumnDefinition<MixedBalanceAccount, MixedBalanceAccount> = {
    name: 'delete',
    title: '',
    component: (account) => (
      <TextButton onClick={ () => onPerformOperation(account, 'delete') } qaMarker='delete-button'>
        Delete
      </TextButton>
    ),
    propsSelector: identity,
  }

  const COLUMNS: TableColumnDefinition<MixedBalanceAccount, any>[] = [
    PROVIDER_NAME_COLUMN,
    ACCOUNT_NAME_COLUMN,
    ACCOUNT_CATEGORY_COLUMN,
    SPACER_COLUMN,
    STATUS_COLUMN,
  ]

  // TODO we don't have any status of balance account that it is updating, only in provider accounts
  // so just show the spinner if some accounts are being updated
  if (isLiveUpdateStarted) {
    COLUMNS.push(LIVE_UPDATE_PRELOADER_COLUMN)
  } else {
    COLUMNS.push(
      UPDATE_CREDENTIALS_BUTTON,
      LINK_BUTTON,
      DELETE_BUTTON,
    )
  }

  const sorting: SortingProp = {
    direction: 'asc',
    columnName: 'provider-name',
  }

  return (
    <section>
      <TypedTable
        columns={ COLUMNS }
        rows={ balanceAccounts }
        sorting={ sorting }
        className={ styles.accountsTable }
        isMobile={ isMobile }
      />
    </section>
  )
}

export default BalanceAccountSettingsModule
